/**
 * @file: The distance that the finger moved in the last touch on the grid.
 *
 * FullCalendar 6.1.21 moves a touched bar to the snap at the centre of the bar.
 * A drag on touch starts after the long press, with no move. The drop then puts
 * the bar at the snap of the centre, not at the snap of the finger. When the
 * snap is shorter than the slot, these two snaps are different. The drop handler
 * reads this class to find a drop from a finger that did not move.
 *
 * The class reads only DOM events. It does not use the internals of FullCalendar.
 */

/**
 * The largest travel in pixels of a finger that holds still.
 *
 * A finger moves some pixels during a long press of one second. A mouse drag
 * starts after 5 px, which is the default of FullCalendar. A finger is less
 * precise than a mouse. Android starts a scroll after a travel of 8 dp.
 */
export const STILL_TOUCH_PX = 8

/** A passive listener does not stop the scroll of the browser. */
const PASSIVE = Object.freeze({ passive: true })

export class TouchTravel {
  constructor () {
    this.root = null
    this.target = null
    this.origin = null
    this.farthest = 0
    this._onTouchStart = domEvent => this.startTouch(domEvent)
    this._onTouchMove = domEvent => this.followTouch(domEvent)
  }

  /**
   * Listen for each touch that starts in the root.
   *
   * @param {Element} root - The calendar element
   */
  attach (root) {
    if (!root || this.root) return

    this.root = root
    root.addEventListener('touchstart', this._onTouchStart, PASSIVE)
  }

  /** Remove all listeners, and forget the last touch. */
  detach () {
    this.stopFollowingTarget()

    if (this.root) {
      this.root.removeEventListener('touchstart', this._onTouchStart, PASSIVE)
      this.root = null
    }

    this.origin = null
    this.farthest = 0
  }

  /**
   * Tell if a gesture ended with a touch that travelled less than STILL_TOUCH_PX.
   *
   * @param {Event} [domEvent] - The event that ended the gesture
   * @returns {boolean} False for a mouse event, and before the first touch
   */
  isStillTouch (domEvent) {
    const fromTouch = Boolean(domEvent && typeof domEvent.type === 'string' && domEvent.type.startsWith('touch'))

    return fromTouch && this.origin !== null && this.farthest < STILL_TOUCH_PX
  }

  // ===== Internals =====

  /**
   * Record the start point of a new touch.
   *
   * The browser sends all moves of a touch to the element of its start. The
   * move listener is on that element and not on the root. FullCalendar can
   * remove the element from the document during a drag. After that, the moves
   * do not get to the root.
   */
  startTouch (domEvent) {
    const point = firstPoint(domEvent)

    this.stopFollowingTarget()
    this.origin = point
    this.farthest = 0

    if (!point || !domEvent.target) return

    this.target = domEvent.target
    this.target.addEventListener('touchmove', this._onTouchMove, PASSIVE)
  }

  followTouch (domEvent) {
    const point = firstPoint(domEvent)

    if (!point || !this.origin) return

    const distance = Math.hypot(point.x - this.origin.x, point.y - this.origin.y)

    this.farthest = Math.max(this.farthest, distance)
  }

  stopFollowingTarget () {
    if (this.target) {
      this.target.removeEventListener('touchmove', this._onTouchMove, PASSIVE)
      this.target = null
    }
  }
}

/** The viewport point of the first finger, or null when the event has no finger. */
function firstPoint (domEvent) {
  const touch = domEvent && domEvent.touches && domEvent.touches[0]

  return touch ? { x: touch.clientX, y: touch.clientY } : null
}
